import global from './GlobalStore';
import _ from 'lodash';
import moment from 'moment';

export default class PodcastStore {
  constructor() {
    this.shows = {};
    this.queryResults = [];
  }

  setShow(showId, show) {
    this.shows[showId] = show;
    this.shows[showId].loaded = moment();
  }

  setShows(shows) {
    let newShows = _.keyBy(shows, 'id');

    Object.keys(newShows).forEach(showId => {
      if (!this.shows[showId]) {
        this.shows[showId] = newShows[showId];
        this.shows[showId].loaded = moment();
      }
    });
  }

  setEpisodeCountForShow(showId, count = 0) {
    if (this.shows[showId]) {
      this.shows[showId].count = count;
    }
  }

  setQueriedResults(queryResults) {
    this.queryResults = queryResults;
  }

  checkLoadedDiff(loaded = 0) {
    // If item was loaded within the last hour, return true
    return moment()
      .subtract(1, 'h')
      .isBefore(moment(loaded));
  }

  getShow(showId, callback) {
    return this.fetchShow(showId, true, callback);
  }

  getShows() {
    return this.shows;
  }

  getQueriedResults() {
    return this.queryResults;
  }

  createShow(file = {}, user = {}) {
    return global()
      .getApi()
      .podcastApi.createClipp(file.name, file.type, file.size, user.id)
      .then(result => {
        if (result && result.status === 200 && result.payload) {
        }
      });
  }

  fetchShow(showId, requireFollowers, callback) {
    const show = this.shows[showId];

    if (show && requireFollowers && show.followers && this.checkLoadedDiff(show.loaded)) {
      callback(show);
    } else {
      return global()
        .getApi()
        .podcastApi.getShow(showId)
        .then(result => {
          if (result && result.status === 200 && result.payload) {
            this.setShow(showId, result.payload);
            callback(result.payload, result.metadata);
          }
        });
    }
  }

  fetchShows(type, limit, callback) {
    return global()
      .getApi()
      .podcastApi.getShows(type, limit)
      .then(result => {
        if (result && result.status === 200 && result.payload) {
          this.setShows(result.payload);
          callback(this.getShows(), result.metadata);
        }
      });
  }

  fetchShowsByGenre(showIds, callback) {
    return global()
      .getApi()
      .podcastApi.fetchShowsByGenre(showIds)
      .then(result => {
        if (result && result.status === 200 && result.payload) {
          this.setShows(result.payload);
          callback(result.payload, result.metadata);
        }
      });
  }

  fetchShowsById(showIds, callback) {
    return global()
      .getApi()
      .podcastApi.fetchShowsById(showIds)
      .then(result => {
        if (result && result.status === 200 && result.payload) {
          this.setShows(result.payload);
          callback(result.payload, result.metadata);
        }
      });
  }

  fetchUserFollowedShows(userId, callback) {
    if (!userId) {
      return null;
    }

    return global()
      .getApi()
      .podcastApi.fetchUserFollowedShows(userId)
      .then(result => {
        if (result && result.status === 200 && result.payload) {
          this.setShows(result.payload);
          callback(result.payload, result.metadata);
        }
      });
  }

  fetchPlaylists(callback) {
    return global()
      .getApi()
      .podcastApi.getFeaturedPlaylists()
      .then(result => {
        if (result && result.status === 200 && result.payload) {
          callback(result.payload);
        }
      });
  }

  queryShowsAndEpisodes(query = '', from = 0, size = 25, order = '', callback) {
    const user =
      global()
        .getUserStore()
        .getUser() || {};

    return global()
      .getApi()
      .podcastApi.queryShowsAndEpisodes(user.id, query, from, size, order.toLowerCase())
      .then(result => {
        if (result && result.status === 200 && result.payload) {
          this.setQueriedResults(result.payload);
          callback(this.getQueriedResults(), result.metadata);
        }
      });
  }

  updateEpisode(episodeId, data, callback) {
    return global()
      .getApi()
      .podcastApi.updateEpisode(episodeId, data)
      .then(result => {
        if (result && result.status === 200 && result.payload) {
          if (result && result.status === 200 && result.payload) {
            callback && callback(result.payload, result.metadata);
          }
        }
      });
  }

  fetchEpisodes(showId, from, size = 1, order, callback) {
    const user =
      global()
        .getUserStore()
        .getUser() || {};

    return global()
      .getApi()
      .podcastApi.getEpisodes(user.id, showId, from, size, order)
      .then(result => {
        if (result && result.status === 200 && result.payload) {
          // setEpisodes?
          this.setEpisodeCountForShow(showId, result.metadata && result.metadata.size);
          callback(result.payload, result.metadata, this.shows[showId]);
        } else {
          callback([]);
        }
      });
  }

  fetchEpisode(episodeId, callback) {
    return global()
      .getApi()
      .podcastApi.getEpisode(episodeId, 1)
      .then(result => {
        if (result && result.status === 200 && result.payload) {
          // setEpisodes?
          callback(result.payload, result.metadata);
        } else {
          callback([]);
        }
      });
  }

  followShow(showId, followerId, callback) {
    return global()
      .getApi()
      .podcastApi.followShow(showId, followerId)
      .then(result => {
        if (result.status === 200 && result.payload) {
          this.fetchShow(showId, false, callback);
        } else {
          console.error('Unable to follow user: ' + result.error);
          callback();
        }
      });
  }

  unfollowShow(showId, followerId, callback) {
    return global()
      .getApi()
      .podcastApi.unfollowShow(showId, followerId)
      .then(result => {
        if (result.status === 200 && result.payload) {
          this.fetchShow(showId, false, callback);
        } else {
          console.error('Unable to follow user: ' + result.error);
          callback();
        }
      });
  }

  searchEpisodesInShow(showId, searchTerm, from, size = 1, order, callback) {
    const user =
      global()
        .getUserStore()
        .getUser() || {};

    return global()
      .getApi()
      .podcastApi.searchEpisodesInShow(user.id, showId, searchTerm, from, size, order)
      .then(result => {
        if (result.status === 200 && result.payload) {
          callback(result.payload, result.metadata);
        } else {
          console.error('Unable to find episodes matching query: ' + searchTerm);
          callback();
        }
      });
  }

  recordEpisodeListen({ userId, showId, episodeId, browserData, episodeData }, callback) {
    const sessionData = JSON.parse(sessionStorage.getItem('clipps_data'));

    return global()
      .getApi()
      .podcastApi.recordEpisodeListen(userId, showId, episodeId, browserData, episodeData, sessionData)
      .then(result => {
        if (result && result.status === 200 && result.payload) {
          callback(result.payload, result.metadata);
        } else {
          callback([]);
        }
      });
  }

  createClipp({ start, end, input, output, title, imageUrl, showId, episodeId }, callback) {
    const user =
      global()
        .getUserStore()
        .getUser() || {};

    return global()
      .getApi()
      .podcastApi.createClipp({
        userId: user.id,
        start,
        end,
        input,
        output,
        title,
        imageUrl,
        showId,
        episodeId,
      })
      .then(result => {
        if (result && result.status === 200 && result.payload) {
          callback(result.payload, result.metadata);
        } else {
          callback([]);
        }
      });
  }

  fetchClippsForEpisode({ episodeId, query, from, size = 1, order }, callback) {
    const user =
      global()
        .getUserStore()
        .getUser() || {};

    return global()
      .getApi()
      .podcastApi.getClippsForEpisode({ userId: user.id, episodeId, query, from, size, order })
      .then(result => {
        if (result && result.status === 200 && result.payload) {
          callback(result.payload, result.metadata);
        } else {
          callback([]);
        }
      });
  }

  fetchClippsForShow({ showId, query, from, size = 1, order }, callback) {
    const user =
      global()
        .getUserStore()
        .getUser() || {};

    return global()
      .getApi()
      .podcastApi.getClippsForShow({ userId: user.id, showId, query, from, size, order })
      .then(result => {
        if (result && result.status === 200 && result.payload) {
          callback(result.payload, result.metadata);
        } else {
          callback([]);
        }
      });
  }
}
